import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useQuery, useMutation, useAction } from 'convex/react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../../../convex/_generated/api'
import {
  Image as ImageIcon,
  Film,
  Play,
  Music2,
  Users,
  Sparkles,
  Loader2,
  Mic,
  ChevronLeft,
  ChevronRight,
  Clock,
  RotateCcw,
  AlertCircle,
  Check,
  Video,
  Headphones,
  Download,
  Globe,
  Search,
  Plus,
  Trash2,
} from 'lucide-react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'
import { Lightbox } from '@/components/ui/lightbox'

const PAGE_SIZE = 24

const GOOGLE_CLIENT_ID = '246664966428-modmi6uf919cgufm0mlei4pkpdlchf4h.apps.googleusercontent.com'
const GOOGLE_API_KEY = 'AIzaSyATobi0FAJgch50jmkwAvbfOF3hp9Qx1-Q'
const GOOGLE_APP_ID = '246664966428'

declare global {
  interface Window {
    Dropbox?: {
      choose(options: {
        success: (files: Array<{ name: string; link: string; bytes: number; icon: string; thumbnailLink?: string; isDir: boolean }>) => void
        cancel?: () => void
        linkType: 'direct' | 'preview'
        multiselect: boolean
        extensions?: string[]
        folderselect?: boolean
        sizeLimit?: number
      }): void
    }
    gapi?: {
      load(api: string, callback: () => void): void
    }
    google?: {
      accounts: {
        oauth2: {
          initTokenClient(config: {
            client_id: string
            scope: string
            callback: (response: { access_token?: string; error?: string }) => void
          }): { requestAccessToken(): void }
        }
      }
      picker: {
        PickerBuilder: new () => GooglePickerBuilder
        ViewId: { DOCS: string }
        DocsView: new () => { setMimeTypes(mimeTypes: string): GooglePickerDocsView }
        Action: { PICKED: string; CANCEL: string }
      }
    }
  }
}

interface GooglePickerDocsView {
  setMimeTypes(mimeTypes: string): GooglePickerDocsView
}

interface GooglePickerBuilder {
  addView(view: string | GooglePickerDocsView): GooglePickerBuilder
  setOAuthToken(token: string): GooglePickerBuilder
  setDeveloperKey(key: string): GooglePickerBuilder
  setAppId(appId: string): GooglePickerBuilder
  setCallback(callback: (data: { action: string; docs?: Array<{ id: string; name: string; mimeType: string; sizeBytes?: number }> }) => void): GooglePickerBuilder
  enableFeature(feature: number): GooglePickerBuilder
  build(): { setVisible(visible: boolean): void }
}

type SortMode = 'date' | 'name'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function cleanAssetName(asset: { url: string; type: string; source: string; name?: string; crawled?: boolean; generator?: { prompt?: string; params?: any } }): string {
  const { url, type, source, name, crawled, generator } = asset

  if (generator?.params?.characterRef) {
    const pose = generator.params.pose
    return pose ? `Character Reference (${pose})` : 'Character Reference'
  }

  if (source === 'extracted') {
    return name || 'Extracted Frame'
  }

  if (generator?.prompt) {
    let text = generator.prompt.trim()
    text = text.replace(/^High quality character portrait photo\.\s*/i, '')
    if (text.length > 0) {
      return text.length > 60 ? text.slice(0, 57) + '...' : text
    }
  }

  if (name && name.length > 0) {
    const cleaned = name.replace(/\.[^.]+$/, '')
    return cleaned.length > 60 ? cleaned.slice(0, 57) + '...' : cleaned
  }

  const filename = url.split('/').pop()?.split('?')[0] ?? 'Asset'
  const nameWithoutExt = filename.replace(/\.[^.]+$/, '')
  const isHash = /^[a-f0-9-]{20,}$/i.test(nameWithoutExt) || /^\d{5,}$/.test(nameWithoutExt)

  if (isHash) {
    const typeLabel = type === 'audio' ? 'Audio' : type === 'video' ? 'Video' : 'Image'
    if (crawled) return `Crawled ${typeLabel}`
    if (source === 'uploaded') return `Uploaded ${typeLabel}`
    return typeLabel
  }

  const cleaned = nameWithoutExt
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
  return cleaned.length > 50 ? cleaned.slice(0, 47) + '...' : cleaned
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return `${Math.floor(days / 30)}mo ago`
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

const sourceLabel = (s: string) =>
  s === 'generated' ? 'Generated' : s === 'uploaded' ? 'Uploaded' : 'Extracted'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function AssetCard({ asset, onOpenLightbox }: { asset: any; onOpenLightbox: (url: string, type: 'image' | 'video') => void }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const deleteAsset = useMutation(api.assets.deleteAsset)

  const name = cleanAssetName(asset)
  const isImage = asset.type === 'image'
  const isVideo = asset.type === 'video'
  const isAudio = asset.type === 'audio'
  const hasDuration =
    (isVideo || isAudio) && typeof asset.metadata?.duration === 'number'

  const openLightbox = () => {
    if (isImage || isVideo) {
      onOpenLightbox(asset.url, isVideo ? 'video' : 'image')
    }
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setConfirmingDelete(true)
  }

  const handleConfirmDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsDeleting(true)
    try {
      await deleteAsset({ assetId: asset._id })
    } catch (err) {
      console.error('Failed to delete asset:', err)
      setIsDeleting(false)
      setConfirmingDelete(false)
    }
  }

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setConfirmingDelete(false)
  }

  return (
    <div className="group bg-card rounded-lg border border-border overflow-hidden shadow-xs hover:shadow-sm hover:border-border/80 transition-all w-full sm:w-auto sm:min-w-[300px] sm:max-w-[360px] sm:flex-1">
      <div
        role="button"
        tabIndex={0}
        onClick={openLightbox}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openLightbox()
          }
        }}
        className="h-[180px] bg-gradient-to-br from-neutral-100 to-neutral-50 relative flex items-center justify-center overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        {isImage ? (
          <>
            <ImageIcon size={28} className="text-muted-foreground/30" />
            <img
              src={asset.url}
              alt={name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          </>
        ) : isVideo ? (
          <>
            <Film size={28} className="text-neutral-500" />
            <video
              src={asset.url}
              preload="metadata"
              muted
              playsInline
              poster={asset.sourceMetadata?.thumbnailUrl}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                <Play size={18} className="text-white fill-white ml-0.5" />
              </div>
            </div>
          </>
        ) : (
          <div className="h-full w-full bg-amber-50 flex items-center justify-center">
            <Music2 size={28} className="text-amber-600/60" />
          </div>
        )}
        {hasDuration && (
          <div className="absolute bottom-2 right-2 pointer-events-none">
            <span
              className={cn(
                'text-[11px] font-medium tabular-nums px-2 py-0.5 rounded leading-normal backdrop-blur-sm',
                isVideo
                  ? 'bg-black/50 text-white/90'
                  : 'bg-amber-900/40 text-amber-100'
              )}
            >
              {formatDuration(asset.metadata!.duration!)}
            </span>
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1 pointer-events-none">
          {asset.source === 'generated' && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-black/40 text-white/90 leading-normal backdrop-blur-sm inline-flex items-center gap-1">
              <Sparkles size={10} />
              Generated
            </span>
          )}
          {asset.crawled && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-black/40 text-white/90 leading-normal backdrop-blur-sm">
              Crawled
            </span>
          )}
        </div>

        {!confirmingDelete && (
          <div className="absolute top-2 right-2 flex flex-col gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-all z-10">
            <button
              onClick={handleDeleteClick}
              className="p-1.5 rounded-md bg-white/90 text-neutral-500 hover:bg-red-50 hover:text-red-600 transition-all shadow-sm"
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}

        {confirmingDelete && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-0 bg-black/60 flex items-center justify-center z-20 animate-in fade-in duration-100"
          >
            <div className="flex flex-col items-center gap-3">
              <span className="text-sm text-white font-medium">Delete this asset?</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-white/20 hover:bg-white/30 rounded-md transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="px-3 py-2.5 flex items-center justify-between gap-2">
        <h3 className="font-medium text-neutral-900 text-sm truncate">{name}</h3>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0 bg-muted text-muted-foreground">
          {asset.workflowTitle}
        </span>
      </div>

      <div className="border-t border-border px-3 py-2 flex items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {sourceLabel(asset.source)}
        </span>
        <span className="text-xs text-muted-foreground/40">&middot;</span>
        <span className="text-xs text-muted-foreground">
          {relativeTime(asset.updatedAt)}
        </span>
        {asset.generator?.model && (
          <>
            <span className="text-xs text-muted-foreground/40">&middot;</span>
            <span className="text-xs text-muted-foreground truncate">
              {asset.generator.model}
            </span>
          </>
        )}
      </div>
    </div>
  )
}

export default function AssetsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(0)
  const [sortBy, setSortBy] = useState<SortMode>('date')
  const [query, setQuery] = useState('')
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [lightboxType, setLightboxType] = useState<'image' | 'video'>('image')
  const [urlImportOpen, setUrlImportOpen] = useState(false)
  const [dropboxImporting, setDropboxImporting] = useState(false)
  const [gdriveImporting, setGdriveImporting] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const assets = useQuery(api.assets.listUserAssets, {})
  const characters = useQuery(api.workflows.listUserCharacters, {})

  const recordUpload = useMutation(api.assets.recordUserUpload)
  const importFromDropbox = useAction(api.cloudStorage.importFromDropboxChooser)
  const importFromGoogleDrive = useAction(api.cloudStorage.importFromGoogleDrivePicker)
  const importFromGoogleDriveRef = useRef(importFromGoogleDrive)
  importFromGoogleDriveRef.current = importFromGoogleDrive

  const isLoading = assets === undefined || characters === undefined

  const allAssets = assets ?? []
  const allCharacters = characters ?? []

  const filteredAssets = useMemo(() => {
    const q = query.trim().toLowerCase()
    const base = allAssets.filter((a) => {
      if (!q) return true
      return cleanAssetName(a).toLowerCase().includes(q)
    })

    if (sortBy === 'name') {
      return [...base].sort((a, b) => {
        const an = cleanAssetName(a).toLowerCase()
        const bn = cleanAssetName(b).toLowerCase()
        return an.localeCompare(bn)
      })
    }
    return [...base].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
  }, [allAssets, sortBy, query])

  // Show characters alongside assets — chat-generated entities live together.
  const tabCharacters = allCharacters

  const totalFilteredCount = filteredAssets.length + tabCharacters.length
  const totalPages = Math.max(1, Math.ceil(totalFilteredCount / PAGE_SIZE))
  const hasPrevPage = page > 0
  const hasNextPage = page < totalPages - 1

  // Reset page when the search filter changes.
  useEffect(() => {
    setPage(0)
  }, [query])

  const { paginatedCharacters, paginatedAssets } = useMemo(() => {
    const start = page * PAGE_SIZE
    const end = start + PAGE_SIZE

    const assetCount = filteredAssets.length
    const assetStart = Math.min(start, assetCount)
    const assetEnd = Math.min(end, assetCount)
    const assts = assetStart < assetEnd ? filteredAssets.slice(assetStart, assetEnd) : []

    const charStart = Math.max(0, start - assetCount)
    const charEnd = Math.max(0, end - assetCount)
    const chars = charStart < charEnd ? tabCharacters.slice(charStart, charEnd) : []

    return { paginatedCharacters: chars, paginatedAssets: assts }
  }, [filteredAssets, tabCharacters, page])

  const hasResults = totalFilteredCount > 0

  const scrollToTop = useCallback(() => {
    const el = scrollRef.current?.closest('.overflow-auto')
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const goToPage = useCallback(
    (p: number) => {
      setPage(p)
      scrollToTop()
    },
    [scrollToTop]
  )

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    const fileList = Array.from(files)
    setUploading(true)
    setUploadProgress({ done: 0, total: fileList.length })

    let failed = 0
    for (let i = 0; i < fileList.length; i++) {
      const f = fileList[i]
      try {
        const res = await fetch('https://footage-ffmpeg-production.up.railway.app/upload-media', {
          method: 'POST',
          body: f,
          headers: {
            'Content-Type': 'application/octet-stream',
            'x-file-name': encodeURIComponent(f.name),
          },
        })
        if (!res.ok) {
          const err = await res.text()
          throw new Error(err)
        }
        const { cdnUrl } = await res.json()
        await recordUpload({
          fileName: f.name,
          cdnUrl,
          fileSize: f.size || undefined,
          mimeType: f.type || undefined,
        })
      } catch (err) {
        failed++
        console.error(`Upload failed for ${f.name}:`, err)
      }
      setUploadProgress({ done: i + 1, total: fileList.length })
    }

    setUploading(false)
    setUploadProgress(null)
    e.target.value = ''
    if (failed > 0) {
      alert(`${failed} file(s) failed to upload. Check console for details.`)
    }
  }, [recordUpload])

  const handleDropboxImport = useCallback(() => {
    if (!window.Dropbox) return
    window.Dropbox.choose({
      success: async (files) => {
        const mediaFiles = files.filter(f => !f.isDir)
        if (mediaFiles.length === 0) return
        setDropboxImporting(true)
        try {
          await importFromDropbox({
            files: mediaFiles.map(f => ({
              name: f.name,
              link: f.link,
              bytes: f.bytes,
              thumbnailLink: f.thumbnailLink,
            })),
          })
        } finally {
          setDropboxImporting(false)
        }
      },
      linkType: 'direct',
      multiselect: true,
      extensions: [
        '.mp4', '.mov', '.avi', '.mkv', '.webm',
        '.jpg', '.jpeg', '.png', '.gif', '.webp',
        '.mp3', '.wav', '.aac', '.flac', '.ogg', '.m4a',
      ],
    })
  }, [importFromDropbox])

  const handleGoogleDriveImport = useCallback(() => {
    if (!window.google?.accounts?.oauth2 || !window.gapi) return

    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'https://www.googleapis.com/auth/drive.file',
      callback: async (tokenResponse) => {
        if (!tokenResponse.access_token) return

        const accessToken = tokenResponse.access_token

        window.gapi!.load('picker', () => {
          const mediaMimeTypes = [
            'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/webm',
            'image/jpeg', 'image/png', 'image/gif', 'image/webp',
            'audio/mpeg', 'audio/wav', 'audio/aac', 'audio/flac', 'audio/ogg', 'audio/mp4',
          ].join(',')

          const view = new window.google!.picker.DocsView()
            .setMimeTypes(mediaMimeTypes)

          const picker = new window.google!.picker.PickerBuilder()
            .addView(view)
            .setOAuthToken(accessToken)
            .setDeveloperKey(GOOGLE_API_KEY)
            .setAppId(GOOGLE_APP_ID)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .setCallback((data: any) => {
              if (data.action === 'picked' && data.docs) {
                setGdriveImporting(true)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                importFromGoogleDriveRef.current({
                  accessToken,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  files: data.docs.map((doc: any) => ({
                    id: doc.id,
                    name: doc.name,
                    mimeType: doc.mimeType,
                    sizeBytes: doc.sizeBytes,
                  })),
                })
                  .then(() => setGdriveImporting(false))
                  .catch(() => setGdriveImporting(false))
              }
            })
            .build()

          picker.setVisible(true)
        })
      },
    })

    tokenClient.requestAccessToken()
  }, [])

  // Handle ?import=... query param (from ActionSidebar)
  useEffect(() => {
    const importType = searchParams.get('import')
    if (!importType) return
    setSearchParams({}, { replace: true })
    switch (importType) {
      case 'upload':
        fileInputRef.current?.click()
        break
      case 'url':
        setUrlImportOpen(true)
        break
      case 'dropbox':
        handleDropboxImport()
        break
      case 'gdrive':
        handleGoogleDriveImport()
        break
    }
  }, [searchParams, setSearchParams, handleDropboxImport, handleGoogleDriveImport])

  // Suppress unused-state warnings — these are still mutated by the
  // upload/import handlers that we keep wired even though the toolbar UI
  // that used to surface them has been removed.
  void dropboxImporting; void gdriveImporting; void uploading; void uploadProgress
  void setSortBy; void urlImportOpen

  return (
    <div ref={scrollRef} className="px-8 pt-10 pb-16">
      <Helmet>
        <title>Assets — Footage</title>
        <meta name="description" content="Browse and manage your uploaded images, characters, and audio files." />
      </Helmet>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="video/*,image/*,audio/*"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Header: title + search */}
      <div className="flex items-start justify-between gap-6 mb-10 max-w-[1100px] mx-auto w-full">
        <div>
          <h1 className="text-[22px] leading-tight font-medium tracking-tight text-foreground">Assets</h1>
          <p className="mt-1 text-[14px] text-muted-foreground">Browse and manage your uploads</p>
        </div>
        <div className="relative shrink-0 hidden md:block">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search assets"
            className="h-10 w-[300px] rounded-full border border-border bg-white pl-10 pr-4 text-[14px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-ring"
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !hasResults ? (
        <div className="max-w-[1100px] mx-auto w-full">
          {/* Decorative skeleton cards — match the library page's empty state */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={cn(
                  'h-[280px] rounded-[20px] bg-gradient-to-b from-zinc-100 to-background',
                  i === 1 && 'hidden sm:block',
                  i === 2 && 'hidden lg:block',
                )}
                aria-hidden
              />
            ))}
          </div>
          <div className="flex flex-col items-center text-center mt-8">
            <p className="text-[15px] text-muted-foreground">
              {query
                ? `No assets match "${query}".`
                : 'No assets yet. Start chatting to upload or import assets.'}
            </p>
            {!query && (
              <Link
                to="/"
                className="mt-5 inline-flex items-center gap-1.5 h-10 px-5 rounded-full border border-border text-[14px] font-medium text-foreground hover:bg-muted/60 transition-colors"
              >
                <Plus className="h-4 w-4" />
                New Video
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-8 max-w-[1100px] mx-auto w-full">
          {paginatedAssets.length > 0 && (
            <section>
              <div className="flex flex-wrap gap-4">
                {paginatedAssets.map((asset) => (
                  <AssetCard
                    key={asset._id}
                    asset={asset}
                    onOpenLightbox={(url, type) => {
                      setLightboxUrl(url)
                      setLightboxType(type)
                    }}
                  />
                ))}
              </div>
            </section>
          )}

          {paginatedCharacters.length > 0 && (
            <section>
              <div className="flex flex-wrap gap-4">
                {paginatedCharacters.map((char) => {
                  const images = char.referenceImages ?? []
                  const heroImage = images[0] ?? null
                  const thumbImages = images.slice(1)
                  return (
                    <div
                      key={`${char.workflowId}-${char.id}`}
                      className="group bg-card rounded-lg border border-border overflow-hidden shadow-xs hover:shadow-sm hover:border-border/80 transition-all w-full sm:w-auto sm:min-w-[300px] sm:max-w-[360px] sm:flex-1"
                    >
                      <div className="h-[180px] bg-gradient-to-br from-neutral-100 to-neutral-50 relative flex items-center justify-center overflow-hidden">
                        {!heroImage && (
                          <Users size={32} className="text-muted-foreground/30" />
                        )}
                        {heroImage && (
                          <img
                            src={heroImage.url}
                            alt={char.name}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                            onClick={() => {
                              setLightboxUrl(heroImage.url)
                              setLightboxType('image')
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                        )}
                        {char.voiceSampleUrl && (
                          <div className="absolute top-2 right-2 z-10">
                            <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-black/50 text-white/90 backdrop-blur-sm inline-flex items-center gap-1">
                              <Mic size={10} />
                              Voice
                            </span>
                          </div>
                        )}
                      </div>
                      {thumbImages.length > 0 && (
                        <div className="flex gap-0.5 bg-neutral-100 border-t border-border">
                          {thumbImages.slice(0, 4).map((img, i) => (
                            <div
                              key={i}
                              role="button"
                              tabIndex={0}
                              className="relative flex-1 h-[56px] overflow-hidden cursor-pointer"
                              onClick={() => {
                                setLightboxUrl(img.url)
                                setLightboxType('image')
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  setLightboxUrl(img.url)
                                  setLightboxType('image')
                                }
                              }}
                            >
                              <img
                                src={img.url}
                                alt={`${char.name} — ${img.pose}`}
                                loading="lazy"
                                className="w-full h-full object-cover hover:brightness-110 transition-all"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none'
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="px-3 py-2.5 flex items-center justify-between gap-2">
                        <h3 className="font-medium text-neutral-900 text-sm truncate">{char.name}</h3>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0 bg-muted text-muted-foreground">
                          {char.workflowTitle}
                        </span>
                      </div>
                      {char.clips && char.clips.length > 0 && (
                        <div className="border-t border-border px-3 py-2 flex items-center gap-1.5 flex-wrap">
                          <Film size={12} className="text-muted-foreground/60 shrink-0" />
                          <span className="text-xs text-muted-foreground">
                            {char.clips.length} {char.clips.length === 1 ? 'clip' : 'clips'}
                          </span>
                          <span className="text-xs text-muted-foreground/40">&middot;</span>
                          {char.clips.slice(0, 2).map((clip, i) => (
                            <span key={clip.id} className="text-xs text-muted-foreground truncate max-w-[100px]">
                              {clip.title}{i < Math.min(char.clips.length, 2) - 1 && ','}
                            </span>
                          ))}
                          {char.clips.length > 2 && (
                            <span className="text-xs text-muted-foreground/60">
                              +{char.clips.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            onClick={() => goToPage(page - 1)}
            disabled={!hasPrevPage || isLoading}
            className={cn(
              'flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
              hasPrevPage && !isLoading
                ? 'text-foreground hover:bg-muted'
                : 'text-muted-foreground/40 cursor-not-allowed'
            )}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => goToPage(page + 1)}
            disabled={!hasNextPage || isLoading}
            className={cn(
              'flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
              hasNextPage && !isLoading
                ? 'text-foreground hover:bg-muted'
                : 'text-muted-foreground/40 cursor-not-allowed'
            )}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <Lightbox url={lightboxUrl} type={lightboxType} onClose={() => setLightboxUrl(null)} />
      <UrlImportDialog open={urlImportOpen} onOpenChange={setUrlImportOpen} />
    </div>
  )
}

// --- URL Import Dialog ---

type ProbeResult = {
  success: true
  title: string
  duration: number
  thumbnail: string
  uploader: string
  videoId: string
} | { success: false; error: string }

function formatSeconds(s: number) {
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = Math.floor(s % 60)
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`
}

function parseTimeInput(val: string): number | undefined {
  if (!val.trim()) return undefined
  const parts = val.split(':').map(Number)
  if (parts.some(isNaN)) return undefined
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
  if (parts.length === 2) return parts[0] * 60 + parts[1]
  if (parts.length === 1) return parts[0]
  return undefined
}

const VIDEO_URL_PATTERN = /^https?:\/\/(www\.)?(youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com|twitch\.tv|twitter\.com|x\.com)/i

function UrlImportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const probeUrl = useAction(api.youtubeImport.probeYoutubeUrl)
  const startImport = useAction(api.youtubeImport.startYoutubeImport)

  const [url, setUrl] = useState('')
  const [probing, setProbing] = useState(false)
  const [probeResult, setProbeResult] = useState<ProbeResult | null>(null)
  const [format, setFormat] = useState<'video' | 'audio'>('video')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [importStarted, setImportStarted] = useState(false)
  const [progress, setProgress] = useState<{ phase: string; percent: number } | null>(null)

  const reset = useCallback(() => {
    setUrl('')
    setProbing(false)
    setProbeResult(null)
    setFormat('video')
    setStartTime('')
    setEndTime('')
    setImporting(false)
    setError(null)
    setImportStarted(false)
    setProgress(null)
  }, [])

  const doProbe = useCallback(async (targetUrl: string) => {
    const trimmed = targetUrl.trim()
    if (!trimmed) return
    setProbing(true)
    setError(null)
    setProbeResult(null)
    setImportStarted(false)
    try {
      const result = await probeUrl({ url: trimmed }) as ProbeResult
      setProbeResult(result)
      if (!result.success) setError(result.error)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch video info')
    }
    setProbing(false)
  }, [probeUrl])

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').trim()
    if (VIDEO_URL_PATTERN.test(pasted)) {
      e.preventDefault()
      setUrl(pasted)
    }
  }, [])

  const handleImport = useCallback(async () => {
    if (!probeResult || !probeResult.success) return
    setImporting(true)
    setError(null)
    setProgress({ phase: 'Starting import...', percent: 0 })

    try {
      const startSec = startTime ? parseTimeInput(startTime) : undefined
      const endSec = endTime ? parseTimeInput(endTime) : undefined
      const result = await startImport({ url: url.trim(), format, startSec, endSec })
      setImporting(false)
      setProgress(null)
      if (result.success) setImportStarted(true)
      else setError(result.error || 'Import failed')
    } catch (e) {
      setImporting(false)
      setProgress(null)
      setError(e instanceof Error ? e.message : 'Import failed')
    }
  }, [probeResult, url, format, startTime, endTime, startImport])

  const hasDuration = probeResult && probeResult.success && probeResult.duration > 0

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset() }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/15 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-[15%] z-50 -translate-x-1/2',
            'w-[calc(100%-2rem)] max-w-[680px]',
            'bg-white rounded-xl shadow-[0_25px_60px_-10px_rgba(0,0,0,0.25)]',
            'border border-neutral-200/80',
            'overflow-hidden flex flex-col',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-[0.98] data-[state=open]:zoom-in-[0.98]',
            'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
            'duration-150'
          )}
        >
          <DialogPrimitive.Title className="sr-only">Import from URL</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Import video or audio from a URL
          </DialogPrimitive.Description>

          {importStarted && probeResult && probeResult.success ? (
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <Check size={18} className="text-emerald-500" />
                </div>
                <div>
                  <p className="text-[15px] font-medium text-neutral-900">Import started</p>
                  <p className="text-xs text-neutral-400 mt-0.5">Processing in the background</p>
                </div>
              </div>

              <div className="flex gap-3 p-3 rounded-lg bg-neutral-50 border border-neutral-200/60">
                {probeResult.thumbnail && (
                  <img src={probeResult.thumbnail} alt="" className="w-20 h-12 object-cover rounded" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">{probeResult.title}</p>
                  <p className="text-xs text-neutral-400 mt-0.5">
                    {format === 'audio' ? 'Audio' : 'Video'}
                    {startTime && ' (trimmed)'}
                    {' -- appears in your assets when done'}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={reset}
                  className="px-3 py-1.5 text-sm rounded-md border border-neutral-200 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                >
                  Import Another
                </button>
                <button
                  onClick={() => { onOpenChange(false); reset() }}
                  className="px-3 py-1.5 text-sm rounded-md bg-neutral-900 text-white hover:bg-neutral-800 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              {probeResult && probeResult.success && probeResult.thumbnail ? (
                <div className="relative">
                  <div className="aspect-video w-full overflow-hidden bg-neutral-100">
                    <img src={probeResult.thumbnail} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-white text-base font-semibold leading-snug line-clamp-2 drop-shadow-lg">
                      {probeResult.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-1.5">
                      {probeResult.uploader && (
                        <span className="text-white/70 text-xs">{probeResult.uploader}</span>
                      )}
                      {hasDuration && (
                        <span className="flex items-center gap-1 text-white/70 text-xs">
                          <Clock size={10} />
                          {formatSeconds(probeResult.duration)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={reset}
                    className="absolute top-3 left-3 px-2 py-1 rounded bg-black/50 backdrop-blur-sm text-white/80 text-xs hover:bg-black/70 hover:text-white transition-colors flex items-center gap-1"
                  >
                    <RotateCcw size={10} />
                    Change
                  </button>
                </div>
              ) : null}

              <div className="p-5 space-y-4">
                {!(probeResult && probeResult.success) && (
                  <>
                    <div className="flex items-center gap-3 border-b border-neutral-200 pb-3">
                      <input
                        type="url"
                        placeholder="Paste a video URL..."
                        value={url}
                        onPaste={handlePaste}
                        onChange={e => { setUrl(e.target.value); setError(null) }}
                        onKeyDown={e => { if (e.key === 'Enter' && url.trim()) { e.preventDefault(); doProbe(url) } }}
                        className="flex-1 bg-transparent text-lg text-neutral-900 placeholder:text-neutral-400 outline-none font-light"
                        autoFocus
                      />
                      <button
                        onClick={() => url.trim() && doProbe(url)}
                        disabled={!url.trim() || probing}
                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-30 transition-all shrink-0"
                      >
                        {probing ? <Loader2 size={14} className="animate-spin" /> : 'Fetch'}
                      </button>
                    </div>

                    {!error && (
                      <div className="py-4 space-y-5">
                        <div className="flex items-center gap-6 justify-center">
                          {[
                            { name: 'YouTube', color: '#FF0000', icon: 'M21.8 8s-.2-1.4-.8-2c-.7-.8-1.6-.8-2-.9C16.2 5 12 5 12 5s-4.2 0-7 .2c-.4 0-1.2.1-2 .9-.6.6-.8 2-.8 2S2 9.6 2 11.2v1.5c0 1.6.2 3.2.2 3.2s.2 1.4.8 2c.8.8 1.8.8 2.2.8 1.6.2 6.8.2 6.8.2s4.2 0 7-.2c.4 0 1.3-.1 2-.9.6-.6.8-2 .8-2s.2-1.6.2-3.2v-1.5c0-1.6-.2-3.1-.2-3.1zM9.9 15V9.2l5.4 2.9L9.9 15z' },
                            { name: 'Vimeo', color: '#1AB7EA', icon: 'M22 7.4c-.1 2.1-1.6 5-4.4 8.7C14.7 20 12.3 22 10.3 22c-1.2 0-2.2-1.1-3-3.4L5.5 12c-.6-2.2-1.2-3.4-1.8-3.4-.1 0-.6.3-1.5.9L1 7.9c1-.9 2-1.7 2.9-2.6 1.3-1.1 2.3-1.7 3-1.8 1.6-.2 2.5.9 2.9 3.2.4 2.5.6 4 .9 4.6.5 2.2 1 3.3 1.5 3.3.4 0 1.1-.7 2-2 .9-1.4 1.4-2.4 1.5-3.2.1-1.2-.4-1.9-1.4-1.9-.5 0-1 .1-1.5.3 1-3.3 2.9-4.9 5.7-4.8 2.1.1 3.1 1.4 2.9 4.4z' },
                            { name: 'Twitter', color: '#000000', icon: 'M18.2 2.25h3.5l-7.7 8.8 9 11.9h-7.1l-5.5-7.2-6.3 7.2H.7l8.2-9.4L0 2.25h7.3l5 6.6 5.9-6.6zm-1.2 18.6h1.9L6.1 4.2H4.1l12.9 16.65z' },
                          ].map(p => (
                            <div key={p.name} className="flex flex-col items-center gap-1.5">
                              <div className="w-9 h-9 rounded-lg bg-neutral-50 border border-neutral-200/60 flex items-center justify-center">
                                <svg viewBox="0 0 24 24" className="w-4 h-4" fill={p.color}>
                                  <path d={p.icon} />
                                </svg>
                              </div>
                              <span className="text-[10px] text-neutral-400">{p.name}</span>
                            </div>
                          ))}
                          <div className="flex flex-col items-center gap-1.5">
                            <div className="w-9 h-9 rounded-lg bg-neutral-50 border border-neutral-200/60 flex items-center justify-center">
                              <Globe className="w-4 h-4 text-neutral-400" />
                            </div>
                            <span className="text-[10px] text-neutral-400">Others</span>
                          </div>
                        </div>
                        <p className="text-xs text-neutral-400 text-center">
                          Paste a URL and press <kbd className="inline-flex items-center justify-center h-4 min-w-[18px] px-1 rounded border border-neutral-200 bg-white text-[10px] font-medium text-neutral-500 mx-0.5">Enter</kbd> to fetch
                        </p>
                      </div>
                    )}
                  </>
                )}

                {error && (
                  <div className="flex items-start gap-2.5 p-3 rounded-lg bg-red-50 border border-red-200/60 text-xs animate-in fade-in slide-in-from-top-1 duration-200">
                    <AlertCircle size={14} className="shrink-0 mt-0.5 text-red-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-red-500 font-medium">Failed to fetch video</p>
                      <p className="text-red-400 mt-1 break-words leading-relaxed">
                        {error.length > 200 ? error.slice(0, 200) + '...' : error}
                      </p>
                    </div>
                    {!probing && (
                      <button
                        onClick={() => doProbe(url)}
                        className="shrink-0 px-2 py-1 rounded bg-red-100 text-red-500 text-xs hover:bg-red-200/70 transition-colors flex items-center gap-1"
                      >
                        <RotateCcw size={10} />
                        Retry
                      </button>
                    )}
                  </div>
                )}

                {probeResult && probeResult.success && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {hasDuration && (
                      <div className="p-3 rounded-lg bg-neutral-50 border border-neutral-200/60">
                        <div className="flex items-center justify-between mb-2.5">
                          <label className="text-[11px] font-semibold text-neutral-400 uppercase tracking-wider">Clip Range</label>
                          <span className="text-[10px] text-neutral-400">{formatSeconds(probeResult.duration)} total</span>
                        </div>
                        <div className="relative h-2 bg-neutral-200 rounded-full mb-3 group">
                          <div
                            className="absolute h-full bg-neutral-900 rounded-full"
                            style={{
                              left: `${((parseTimeInput(startTime) || 0) / probeResult.duration) * 100}%`,
                              right: `${100 - ((parseTimeInput(endTime) || probeResult.duration) / probeResult.duration) * 100}%`,
                            }}
                          />
                          <input
                            type="range"
                            min={0}
                            max={probeResult.duration}
                            step={1}
                            value={parseTimeInput(startTime) || 0}
                            onChange={e => setStartTime(formatSeconds(Number(e.target.value)))}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            style={{ clipPath: 'inset(0 50% 0 0)' }}
                          />
                          <input
                            type="range"
                            min={0}
                            max={probeResult.duration}
                            step={1}
                            value={parseTimeInput(endTime) || probeResult.duration}
                            onChange={e => setEndTime(formatSeconds(Number(e.target.value)))}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            style={{ clipPath: 'inset(0 0 0 50%)' }}
                          />
                          <div
                            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-neutral-900 rounded-full shadow-sm cursor-grab active:cursor-grabbing hover:scale-110 transition-transform"
                            style={{ left: `calc(${((parseTimeInput(startTime) || 0) / probeResult.duration) * 100}% - 8px)` }}
                          />
                          <div
                            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-neutral-900 rounded-full shadow-sm cursor-grab active:cursor-grabbing hover:scale-110 transition-transform"
                            style={{ left: `calc(${((parseTimeInput(endTime) || probeResult.duration) / probeResult.duration) * 100}% - 8px)` }}
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <label className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1 block">Start</label>
                            <input
                              type="text"
                              placeholder="0:00"
                              value={startTime}
                              onChange={e => setStartTime(e.target.value)}
                              className="w-full px-2.5 py-1.5 text-sm rounded-md border border-neutral-200 bg-white text-center font-mono text-neutral-900 outline-none focus:ring-2 focus:ring-neutral-300 focus:border-neutral-300 transition-all"
                            />
                          </div>
                          <div className="flex items-center pt-4">
                            <div className="w-6 h-px bg-neutral-200" />
                          </div>
                          <div className="flex-1">
                            <label className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1 block">End</label>
                            <input
                              type="text"
                              placeholder={formatSeconds(probeResult.duration)}
                              value={endTime}
                              onChange={e => setEndTime(e.target.value)}
                              className="w-full px-2.5 py-1.5 text-sm rounded-md border border-neutral-200 bg-white text-center font-mono text-neutral-900 outline-none focus:ring-2 focus:ring-neutral-300 focus:border-neutral-300 transition-all"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-3">
                      <div className="flex gap-1.5 p-1 rounded-lg bg-neutral-100 border border-neutral-200/60 flex-1">
                        <button
                          onClick={() => setFormat('video')}
                          className={cn(
                            "flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md transition-all duration-200",
                            format === 'video'
                              ? "bg-white text-neutral-900 shadow-sm font-medium"
                              : "text-neutral-500 hover:text-neutral-700"
                          )}
                        >
                          <Video size={14} /> Video
                        </button>
                        <button
                          onClick={() => setFormat('audio')}
                          className={cn(
                            "flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md transition-all duration-200",
                            format === 'audio'
                              ? "bg-white text-neutral-900 shadow-sm font-medium"
                              : "text-neutral-500 hover:text-neutral-700"
                          )}
                        >
                          <Headphones size={14} /> Audio Only
                        </button>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0 min-w-[140px]">
                        {progress && importing && (
                          <div className="space-y-1">
                            <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                              <div
                                className="h-full bg-neutral-900 rounded-full transition-all duration-300"
                                style={{ width: `${progress.percent}%` }}
                              />
                            </div>
                            <p className="text-[10px] text-neutral-400 text-center">{progress.phase}</p>
                          </div>
                        )}
                        <button
                          onClick={handleImport}
                          disabled={importing}
                          className="flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40 transition-all duration-200"
                        >
                          {importing ? (
                            <><Loader2 size={14} className="animate-spin" /> {progress ? progress.phase.split('...')[0] + '...' : 'Importing...'}</>
                          ) : (
                            <><Download size={14} /> Import</>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
